import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { formatCents } from '../lib/money';
import { periodLabel } from './Earnings';
import { runnerTheme } from '../styles/runnerTheme';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Spinner, Typography } from '@glplus/glpweb-dsl';
import { RunnerAction } from '../components/RunnerAction';
import { RunnerPageHeading } from '../components/RunnerPageHeading';
import { Panel } from '../components/Panel';
import { runnerApi, RunnerApiError, type BankingState, type EarningsResponse } from '../lib/runnerApiClient';

/**
 * §5 stage 7 — banking and tax. Stripe Connect Express onboarding, required
 * before go-live so every active Runner is payable.
 *
 * 🔑 We show Stripe's own outstanding requirements, translated. A Runner told
 * only "pending" has nowhere to go; a Runner told "add your bank account" can
 * finish. Unknown keys are humanised rather than hidden, because an unfamiliar
 * requirement the Runner can still act on beats a silent omission.
 *
 * 🛑 `payable` is never re-derived here. It is computed once, server-side, from
 * Stripe's payouts_enabled. Recomputing it in the UI from detailsSubmitted is
 * exactly how "I finished the form" gets mistaken for "I can be paid".
 */

/** Stripe requirement keys we have seen; anything else falls back to a
 *  humanised key so a new requirement is still actionable. */
const REQUIREMENT_KEYS: Record<string, string> = {
  external_account: 'banking.req.bankAccount',
  'business_profile.url': 'banking.req.businessUrl',
  'business_profile.mcc': 'banking.req.businessType',
  'individual.first_name': 'banking.req.firstName',
  'individual.last_name': 'banking.req.lastName',
  'individual.dob.day': 'banking.req.dob',
  'individual.dob.month': 'banking.req.dob',
  'individual.dob.year': 'banking.req.dob',
  'individual.address.line1': 'banking.req.address',
  'individual.address.city': 'banking.req.address',
  'individual.address.postal_code': 'banking.req.address',
  'individual.ssn_last_4': 'banking.req.ssnLast4',
  'individual.id_number': 'banking.req.idNumber',
  'individual.verification.document': 'banking.req.idDocument',
  'individual.email': 'banking.req.email',
  'individual.phone': 'banking.req.phone',
  'tos_acceptance.date': 'banking.req.tos',
  'tos_acceptance.ip': 'banking.req.tos',
};

/** 'individual.address.line1' -> 'Individual address line1' */
function humanise(key: string): string {
  const s = key.replace(/[._]/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function Banking({ canReadEarnings = false }: { canReadEarnings?: boolean }) {
  const navigate = useNavigate();
  const [payouts, setPayouts] = useState<EarningsResponse | null>(null);
  const [payoutError, setPayoutError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const es = i18n.language.startsWith('es');
  const [state, setState] = useState<BankingState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const autoRefreshed = useRef(false);
  const loadGeneration = useRef(0);
  const [refreshing, setRefreshing] = useState(false);

  const returnUrl = `${window.location.origin}/banking?done=1`;
  const refreshUrl = `${window.location.origin}/banking?refresh=1`;

  const load = useCallback(async () => {
    const generation = ++loadGeneration.current;
    setRefreshing(true);
    setError(null);
    try {
      const [bank, ledger] = await Promise.allSettled([
        runnerApi.banking(),
        canReadEarnings ? runnerApi.earnings({ limit: 1 }) : Promise.resolve(null),
      ]);
      if (generation !== loadGeneration.current) return;
      if (ledger.status === 'fulfilled') { setPayouts(ledger.value); setPayoutError(null); }
      else setPayoutError(t('earnings.loadFailed'));
      if (bank.status === 'rejected') throw bank.reason;
      setState(bank.value);
    } catch (e) {
      if (generation === loadGeneration.current) {
        setError(e instanceof RunnerApiError ? e.message : t('banking.loadFailed'));
      }
    } finally {
      if (generation === loadGeneration.current) setRefreshing(false);
    }
  }, [t, canReadEarnings]);

  const start = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const { onboardingUrl } = await runnerApi.startBanking({ returnUrl, refreshUrl });
      // 🪤 The link is single-use and short-lived. Navigate immediately —
      // never store it, never render it as a copyable URL.
      window.location.assign(onboardingUrl);
    } catch (e) {
      setError(e instanceof RunnerApiError ? e.message : t('banking.startFailed'));
      setBusy(false);
    }
  }, [returnUrl, refreshUrl, t]);

  useEffect(() => {
    void load();
    return () => { loadGeneration.current += 1; };
  }, [load]);

  useEffect(() => {
    // Stripe sends the Runner to refresh_url when the link has already expired.
    // The contract is to MINT A NEW ONE and continue — showing an error here
    // would strand them on a dead end that is not their fault. Guarded by a ref
    // so a re-render cannot loop.
    const params = new URLSearchParams(window.location.search);
    if (params.get('refresh') === '1' && !autoRefreshed.current) {
      autoRefreshed.current = true;
      void start();
    }
  }, [start]);

  if (state === null && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <Spinner />
      </Box>
    );
  }

  const s = state?.status;
  const due = s?.requirementsDue ?? [];
  const justReturned = new URLSearchParams(window.location.search).get('done') === '1';

  return (
    <Box data-testid="page-banking">
      <RunnerPageHeading title={es ? 'Banco y pagos' : 'Banking & Payouts'} subtitle={t('banking.subtitle')} topic="earnings" onRefresh={() => void load()} refreshing={refreshing} />

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {justReturned && !state?.payable && !error && (
        // Returning from Stripe does NOT mean verified. Saying so plainly beats
        // a success banner that the next screen contradicts.
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">{t('banking.returnedPending')}</Alert>
        </Box>
      )}

      {state && <Panel title={t('banking.statusTitle')} accent={runnerTheme.topics.earnings.accent} dataTestId="banking-status">
        <Typography sx={{ fontWeight: 800, fontSize: '16px', color: state.payable ? '#2E7D32' : '#B45309' }}>
          {state.payable ? t('banking.payable') : state.started ? t('banking.inProgress') : t('banking.notStarted')}
        </Typography>
        <Typography sx={{ color: '#5f6b7a', fontSize: '13px', mt: 0.5 }}>
          {state.payable ? t('banking.payableBody') : t('banking.notPayableBody')}
        </Typography>

        {s?.disabledReason && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning">{t('banking.disabled', { reason: s.disabledReason })}</Alert>
          </Box>
        )}

        {due.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1f2937', mb: 0.75 }}>
              {t('banking.stillNeeded')}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'grid', gap: 0.4 }}>
              {[...new Set(due.map((k) => REQUIREMENT_KEYS[k] ?? k))].map((k) => (
                <Typography key={k} component="li" sx={{ color: '#5f6b7a', fontSize: '13px' }}>
                  {k.startsWith('banking.req.') ? t(k) : humanise(k)}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

      </Panel>}
      <Box sx={{ mt: 2 }}>
        <Panel title={es ? 'Información bancaria' : 'Banking information'} accent={runnerTheme.topics.earnings.accent}>
          <Typography sx={{ color: runnerTheme.muted }}>{es ? 'Administra tu información bancaria de forma segura con Stripe.' : 'Manage your banking information securely with Stripe.'}</Typography>
        </Panel>
      </Box>
      {state && <Box sx={{ mt: 2.5 }}>
          <RunnerAction topic="earnings" appearance="primary"
            ariaLabel={state.started ? t('banking.continue') : t('banking.start')}
            buttonText={state.started ? t('banking.continue') : t('banking.start')}
            size="small"
            onClick={() => void start()}
            disabled={busy}
          />
          <Typography sx={{ color: '#5f6b7a', fontSize: '12px', mt: 1 }}>{t('banking.stripeNote')}</Typography>
      </Box>}
      {canReadEarnings && <Box sx={{ display: 'grid', gap: 2, mt: 2 }}>
        <Panel title={es ? 'Último pago' : 'Latest payout'} accent={runnerTheme.topics.earnings.accent} dataTestId="banking-latest-payout">
          {payoutError && <Alert severity="error">{payoutError}</Alert>}
          {payouts ? payouts.settlements.length ? <Typography>{periodLabel(payouts.settlements[0], i18n.language)} · {formatCents(payouts.settlements[0].netCents, i18n.language)} · {t(`earnings.status.${payouts.settlements[0].status}`)}</Typography> : <Typography>{t('earnings.noStatements')}</Typography> : !payoutError && <Spinner />}
        </Panel>
        <Panel title={es ? 'Historial de pagos' : 'Payout history'} accent={runnerTheme.topics.earnings.accent}>
          <Typography sx={{ mb: 2 }}>{es ? 'Consulta tus estados de cuenta anteriores y el estado de cada pago.' : 'View previous statements and each payment’s status.'}</Typography>
          <RunnerAction topic="earnings" buttonText={es ? 'Ver pagos' : 'View payouts'} ariaLabel={es ? 'Ver pagos' : 'View payouts'} onClick={() => navigate('/earnings')} />
        </Panel>
      </Box>}
    </Box>
  );
}
